'use client';

import { CheckCircle2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useId, useState } from 'react';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/app/components/ui/dialog';
import { Input } from '@/app/components/ui/input';
import { isValidWorkUrl } from '../_utils';

type WorkSubmitDialogProps = {
  open: boolean;
  submitting?: boolean;
  success?: boolean;
  submitError?: string;
  onClose: () => void;
  onSubmit: (workUrl: string) => Promise<void> | void;
};

export function WorkSubmitDialog({
  open,
  submitting = false,
  success = false,
  submitError,
  onClose,
  onSubmit,
}: WorkSubmitDialogProps) {
  const t = useTranslations('earn.activity');
  const inputId = useId();
  const [workUrl, setWorkUrl] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setWorkUrl('');
      setError('');
    }
  }, [open]);

  const handleSubmit = async () => {
    const nextWorkUrl = workUrl.trim();

    if (!isValidWorkUrl(nextWorkUrl)) {
      setError(t('workUrlInvalid'));
      return;
    }

    setError('');
    try {
      await onSubmit(nextWorkUrl);
    } catch {
      setError(submitError || t('uploadFailed'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="max-w-[480px]" onClose={onClose}>
        <DialogHeader>
          <DialogTitle id="earn-work-submit-title">{t('uploadTitle')}</DialogTitle>
        </DialogHeader>

        {success ? (
          <div className="text-center">
            <CheckCircle2 className="mx-auto size-12 text-system-success" />
            <p className="mt-4 font-medium text-system-text01 text-xl">{t('submitted')}</p>
            <Button type="button" variant="primary" size="dialog" onClick={onClose} className="mt-7 w-full">
              {t('confirm')}
            </Button>
          </div>
        ) : (
          <div>
            <label htmlFor={inputId} className="block font-medium text-sm text-system-text01">
              {t('workLink')}
            </label>
            <Input
              id={inputId}
              type="url"
              value={workUrl}
              onChange={(event) => {
                setWorkUrl(event.target.value);
                setError('');
              }}
              placeholder={t('workUrlPlaceholder')}
              aria-invalid={Boolean(error)}
              className="mt-3 h-12 text-system-text01 aria-[invalid=true]:border-system-error1"
            />
            {error && <p className="mt-2 text-sm text-system-error1">{error}</p>}
            <DialogFooter className="mt-7">
              <Button type="button" variant="outline" size="dialog" onClick={onClose} className="sm:flex-1">
                {t('cancel')}
              </Button>
              <Button
                type="button"
                variant="primary"
                size="dialog"
                onClick={handleSubmit}
                disabled={submitting}
                loading={submitting}
                className="sm:flex-1"
              >
                {submitting ? t('submitting') : t('confirm')}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
